import { ChangeDetectorRef, Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import { ColumnConfig } from '../../backoffice/components/dashboard/table-config';
import { RegistryTableComponent } from '../../core/registry-table/registry-table.component';
// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { escapeHtml } from '../../core/validators/no-special-chars.validator';

// Mocked checklist interface
interface Checklist {
  id: string;
  name: string;
  version: string;
  type: string;
  trusteeTypesLabel?: string;
  lastModified: Date;
  validTo: Date;
}

@Component({
  selector: 'app-list-checklist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    Toast,
    ConfirmDialogModule,
    BreadcrumbModule,
    ToggleSwitchModule,
    RegistryTableComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './list-checklist.component.html',
  styleUrls: ['./list-checklist.component.scss'],
})
export class ListChecklistComponent implements OnInit {
  private titleService = inject(Title);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private questionnaireService = inject(QuestionnaireService);
  private formDropdownService = inject(FormDropdownService);
  private cdr = inject(ChangeDetectorRef);

  // Table configuration
  tableTitle = 'Checklist Attive';
  tableIcon = 'pi-check-square';
  columns: ColumnConfig[] = [
    { title: 'Nome', field: 'name', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    { title: 'Versione', field: 'version', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
    { title: 'Tipo', field: 'type', type: 'tag', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca tipo' },
    { title: 'Stato', field: 'status', type: 'tag', sortable: true, filterType: 'select', filterPlaceholder: 'Filtra stato',
      filterOptions: [{ label: 'Attivo', value: 'ATTIVO' }, { label: 'Non Attivo', value: 'NONATTIVO' }] },
    { title: 'Fiduciari', field: 'trusteeTypesLabel', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca fiduciari' },
    { title: 'Ultima Modifica', field: 'lastModified', sortable: true, filterType: 'date', filterPlaceholder: 'Filtra data' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  showDeleted = signal<boolean>(false);

  breadcrumbItems = signal<MenuItem[]>([
    { label: 'Checklist' },
  ]);

  ngOnInit(): void {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Gestione Checklist - Portale Fiduciari AXA');
    
    // Pre-carica le categorie fiduciario per decodifica sincrona
    this.formDropdownService.preloadTrusteeCategories().subscribe();
    
    this.loadChecklists();
  }

  private loadChecklists(): void {
    this.loading.set(true);
    const observable = this.showDeleted()
      ? this.questionnaireService.getFileChecklistsWithDeleted()
      : this.questionnaireService.getFileChecklists();

    observable.subscribe({
      next: (data) => {
        const mappedData = (data || []).map((item: any) => ({
          id: item.id,
          name: item.name,
          version: item.version || '',
          type: item.type || 'FILE',
          trusteeTypesLabel: this.decodeTrusteeTypes(item.trusteeTypes || (item.trusteeType ? [item.trusteeType] : [])),
          status: this.getChecklistStatus(item.validTo),
          lastModified: item.lastModified ? new Date(item.lastModified) : new Date(),
          validTo: item.validTo ? new Date(item.validTo) : new Date(),
        }));
        this.data.set(mappedData);
        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le checklist',
          life: 5000,
        });
        this.data.set([]);
        this.loading.set(false);
      },
    });
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onShowDeletedChange(): void {
    this.loadChecklists();
  }

  onEditClick(rowData: any): void {
    if (rowData?.id) {
      this.router.navigate(['/create-file-checklist', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    if (!rowData?.id) return;

    this.confirmationService.confirm({
      message: `Sei sicuro di voler eliminare la checklist "${escapeHtml(rowData.name)}"?`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.questionnaireService.deleteChecklist(rowData.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Checklist eliminata',
              detail: `La checklist "${rowData.name}" è stata eliminata con successo`,
              life: 3000,
            });
            this.loadChecklists();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare la checklist',
              life: 5000,
            });
          },
        });
      },
    });
  }

  onExport(): void {
    console.log('Export checklist');
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'Funzionalità in fase di sviluppo',
      life: 3000,
    });
  }

  onClearFilters(): void {
    console.log('Filtri azzerati');
  }

  private getChecklistStatus(validTo: Date | string): string {
    const today = new Date().toISOString().slice(0, 10);
    const validToStr = validTo instanceof Date ? validTo.toISOString().slice(0, 10) : validTo;
    return validToStr >= today ? 'ATTIVO' : 'NONATTIVO';
  }

  private decodeTrusteeTypes(trusteeTypes: string[]): string {
    return trusteeTypes
      .map((trusteeType) => this.formDropdownService.decodeProfessionalTypeSync(trusteeType))
      .join(', ');
  }
}
